import { useState, useEffect, useMemo } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Grid,
  Tooltip,
  useMediaQuery,
  Paper,
} from '@mui/material';
import {
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Shield as ShieldIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  CloudDone as CloudDoneIcon,
} from '@mui/icons-material';

interface HeartbeatPayload {
  status: string;
  service: string;
  version: string;
  timestamp: string;
}

export default function App() {
  // Detect system dark mode preference
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = useState<'light' | 'dark'>(systemPrefersDark ? 'dark' : 'light');

  // Update theme mode if system preference changes and user hasn't explicitly set it
  useEffect(() => {
    setMode(systemPrefersDark ? 'dark' : 'light');
  }, [systemPrefersDark]);

  // Create MUI theme based on selected mode
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'dark' ? '#90caf9' : '#1976d2',
          },
          secondary: {
            main: '#f50057',
          },
          background: {
            default: mode === 'dark' ? '#0f172a' : '#f8fafc',
            paper: mode === 'dark' ? '#1e293b' : '#ffffff',
          },
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          h6: {
            fontWeight: 600,
          },
        },
        shape: {
          borderRadius: 12,
        },
      }),
    [mode]
  );

  const toggleThemeMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Heartbeat connection state
  const [loading, setLoading] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'failed'>('idle');
  const [heartbeatData, setHeartbeatData] = useState<HeartbeatPayload | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Determine API Base URL from environment variables or fallback
  const getApiUrl = (): string => {
    const envUrl =
      import.meta.env.VITE_API_URL ||
      import.meta.env.PUBLIC_API_URL ||
      (window as any).env?.PUBLIC_API_URL;
    return envUrl && envUrl.trim() !== '' ? envUrl.trim().replace(/\/$/, '') : 'http://localhost:8080';
  };

  const checkHeartbeat = async () => {
    setLoading(true);
    setErrorMessage(null);
    const baseUrl = getApiUrl();
    const heartbeatUrl = `${baseUrl}/heartbeat`;

    try {
      const response = await fetch(heartbeatUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const data: HeartbeatPayload = await response.json();
      setHeartbeatData(data);
      setConnectionStatus('success');
    } catch (err: any) {
      console.error('Heartbeat fetch error:', err);
      setConnectionStatus('failed');
      setErrorMessage(err.message || 'Failed to connect to Google Cloud Run backend');
      setHeartbeatData(null);
    } finally {
      setLoading(false);
    }
  };

  // Check connection on component mount
  useEffect(() => {
    checkHeartbeat();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Top Navigation Bar */}
        <AppBar position="static" elevation={2}>
          <Toolbar>
            <ShieldIcon sx={{ mr: 1.5, fontSize: 28 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
              Top Heroes Guild Helper
            </Typography>
            <Tooltip title={`Switch to ${mode === 'light' ? 'Dark' : 'Light'} Mode`}>
              <IconButton color="inherit" onClick={toggleThemeMode}>
                {mode === 'dark' ? <LightIcon /> : <DarkIcon />}
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        {/* Main Content Area */}
        <Container maxWidth="md" sx={{ mt: 5, mb: 5, flexGrow: 1 }}>
          <Grid container spacing={3}>
            {/* Connection Test Status Card */}
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardHeader
                  avatar={<CloudDoneIcon color="primary" sx={{ fontSize: 32 }} />}
                  title={<Typography variant="h6">Google Cloud Backend Connection</Typography>}
                  subheader={`Target URL: ${getApiUrl()}`}
                  action={
                    <Button
                      variant="outlined"
                      startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={checkHeartbeat}
                      disabled={loading}
                      sx={{ borderRadius: 8, mt: 1 }}
                    >
                      {loading ? 'Testing...' : 'Test Connection'}
                    </Button>
                  }
                />
                <Divider />
                <CardContent sx={{ pt: 3, pb: 3 }}>
                  {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body1" color="text.secondary">
                        Connecting to Cloud Run service...
                      </Typography>
                    </Box>
                  )}

                  {!loading && connectionStatus === 'success' && (
                    <Alert
                      severity="success"
                      icon={<CheckCircleIcon fontSize="inherit" />}
                      sx={{ mb: 2, borderRadius: 2 }}
                    >
                      <AlertTitle sx={{ fontWeight: 700 }}>Connection Successful</AlertTitle>
                      The Cloud Run backend answered the heartbeat check cleanly.
                    </Alert>
                  )}

                  {!loading && connectionStatus === 'failed' && (
                    <Alert
                      severity="error"
                      icon={<ErrorIcon fontSize="inherit" />}
                      sx={{ mb: 2, borderRadius: 2 }}
                    >
                      <AlertTitle sx={{ fontWeight: 700 }}>Connection Failed</AlertTitle>
                      {errorMessage || 'Could not reach backend service at target URL.'}
                    </Alert>
                  )}

                  {!loading && connectionStatus === 'idle' && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      Click "Test Connection" to trigger a heartbeat check against Google Cloud Run.
                    </Alert>
                  )}

                  {/* Details Breakdown */}
                  {heartbeatData && (
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, mt: 2, backgroundColor: mode === 'dark' ? '#0f172a' : '#f1f5f9' }}
                    >
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        HEARTBEAT PAYLOAD DETAILS
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip label={heartbeatData.status} color="success" size="small" />
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Service Name
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {heartbeatData.service}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Backend Version
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            v{heartbeatData.version}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="caption" color="text.secondary">
                            Server Timestamp
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {new Date(heartbeatData.timestamp).toLocaleTimeString()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Application Overview Card */}
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Welcome to Top Heroes Guild Helper
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    This application tracks and manages guild rosters, member ranks, notes, and permissions for the game Top Heroes.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                    <Chip label="Rust Backend (Cloud Run)" variant="outlined" color="primary" size="small" />
                    <Chip label="React + Material UI" variant="outlined" color="primary" size="small" />
                    <Chip label="Cloudflare Pages" variant="outlined" color="primary" size="small" />
                    <Chip label="Supabase DB" variant="outlined" color="primary" size="small" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            mt: 'auto',
            textAlign: 'center',
            backgroundColor: mode === 'dark' ? '#1e293b' : '#e2e8f0',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Top Heroes Guild Helper © {new Date().getFullYear()} — Environment: {import.meta.env.MODE}
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
